import React, { useState, useContext, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import { ProductContext } from '../../../../pages/ProductStencil';
import { IProductStencilContext } from '../../product-stencil.types';
import Video from './Video';
import Image from './Image';
import VideoThumbnail from './VideoThumbnail';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

export const ProductGallery: React.FC = () => {
  const productData = useContext(ProductContext);
  const { referenceImage, packagings, overviewAssets, renditionFallback } = productData.product as IProductStencilContext["product"];

  const images = [...packagings, ...overviewAssets];
  const [selectedImage, setSelectedImage] = useState<string>(referenceImage?.largeUrl || renditionFallback);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLightboxEnabled, setIsLightboxEnabled] = useState(true);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);

  const mainCarouselRef = useRef<Slider | null>(null);
  const thumbnailCarouselRef = useRef<Slider | null>(null);

  useEffect(() => {
    const isTouchDevice = navigator.maxTouchPoints > 0;
    const isMobile = window.innerWidth < 1000;

    setIsLightboxEnabled(!(isTouchDevice || isMobile));

    const handleResize = () => {
      const isMobile = window.innerWidth < 1000;
      setIsLightboxEnabled(!(isTouchDevice || isMobile));
    };

    window.addEventListener('resize', handleResize);

    if (productData.activeProductIndex !== currentIndex) {
      setCurrentIndex(productData.activeProductIndex || 0);

      const asset = images[productData.activeProductIndex || 0];
      if (asset) {
        if ('image' in asset) {
          setSelectedImage(asset.image?.extraLargeUrl || asset.image?.largeUrl || renditionFallback);
        } else if ('video' in asset) {
          setSelectedImage(asset.video?.id || renditionFallback);
        }
      }

      if (mainCarouselRef.current) {
        mainCarouselRef.current.slickGoTo(productData.activeProductIndex || 0);
      }

      if (thumbnailCarouselRef.current) {
        thumbnailCarouselRef.current.slickGoTo(productData.activeProductIndex || 0);
      }
    }

    if (isPopupOpen && isLightboxEnabled) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = 'auto';
    };
  }, [productData.activeProductIndex, currentIndex, isPopupOpen, images, renditionFallback, isLightboxEnabled]);

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    if (mainCarouselRef.current) {
      mainCarouselRef.current.slickGoTo(index);
    }
    if (thumbnailCarouselRef.current) {
      thumbnailCarouselRef.current.slickGoTo(index);
    }
  };

  const handleSlideChange = (index: number) => {
    setCurrentIndex(index);
    setPlayingVideo(null);
    const asset = images[index];
    if (asset) {
      if ('image' in asset) {
        setSelectedImage(asset.image?.extraLargeUrl || asset.image?.largeUrl || renditionFallback);
      } else if ('video' in asset) {
        setSelectedImage(asset.video?.id || renditionFallback);
      }
    }

    if (thumbnailCarouselRef.current) {
      thumbnailCarouselRef.current.slickGoTo(index);
    }
  };

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    focusOnSelect: true,
    arrows: images.length > 1,
    beforeChange: (current: number, next: number) => handleSlideChange(next),
    responsive: [
      {
        breakpoint: 1000,
        settings: {
          dots: true,
        }
      },
      {
        breakpoint: 1001,
        settings: {
          dots: false,
        }
      }
    ]
  };

  const thumbnailSettings = {
    infinite: true, 
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    focusOnSelect: true,
    beforeChange: (current: number, next: number) => {
      if (mainCarouselRef.current) {
        mainCarouselRef.current.slickGoTo(next);
      }
    },
  };

  const handleImageClick = (index: number) => {
    const asset = images[index];
    if (asset) {
      if ('image' in asset) {
        setSelectedImage(asset.image?.extraLargeUrl || asset.image.largeUrl || renditionFallback);
      } else if ('video' in asset) {
        setSelectedImage(asset.video.id || renditionFallback);
      }
    }

    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <div className="product-gallery">
      <div className="main-carousel">
        <Slider {...settings} ref={mainCarouselRef}>
          {images.map((asset, index) => (
            <div key={index} className="carousel-item">
              {'image' in asset && asset.image && asset.image.largeUrl ? (
                <Image
                  src={asset.image.largeUrl}
                  alt={asset.image?.altText || 'image'}
                  mainMedia={true}
                  onPopupOpen={() => handleImageClick(index)}
                />
              ) : 'video' in asset && asset.video && asset.video.id ? (
                <Video
                  videoItem={{ ...asset.video, provider: asset.video.type, thumbnail: asset.video.thumbnail || renditionFallback }}
                  isPlaying={playingVideo === asset.video.id}
                  onPlay={() => setPlayingVideo(asset.video.id)}
                />
              ) : (
                <img src={renditionFallback} alt="Fallback" />
              )}
            </div>
          ))}
        </Slider>
      </div>

      <div className={`thumbnail-carousel ${images.length > 4 ? '':'not-infinity'}`}>
        <Slider {...thumbnailSettings} ref={thumbnailCarouselRef}>
          {images.map((asset, index) => (
            <div key={index} className="thumbnail-item" onClick={() => handleThumbnailClick(index)}>
              {'image' in asset && asset.image?.smallUrl ? (
                <img
                  src={asset.image.smallUrl}
                  alt={asset.image?.altText || `Thumbnail ${index + 1}`}
                  className={currentIndex === index ? 'active-thumbnail' : ''}
                />
              ) : 'video' in asset && asset.video?.id ? (
                <VideoThumbnail videoId={asset.video.id} provider={asset.video.type} isActive={currentIndex === index} thumbnail={asset.video.thumbnail || renditionFallback } />
              ) : (
                <img src={renditionFallback} alt="Fallback" className={currentIndex === index ? 'active-thumbnail' : ''} />
              )}
            </div>
          ))}
        </Slider>
      </div>

      {isPopupOpen && isLightboxEnabled && (
        <Lightbox
          mainSrc={selectedImage}
          onCloseRequest={closePopup}
          wrapperClassName="product-gallery__zoom-popup"
          imagePadding={0}
        />
      )}
    </div>
  );
};
